import React, { useState } from "react";

import {
    Box, 
    Paper,
    Divider,
    TextField,
    Grid,
    Typography,
    Switch,
    FormControlLabel,
    styled,
    Card
} from '@mui/material';

import {
  LoadingButton
} from '@mui/lab';

import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonSectionHeader from "../../common/CommonSectionHeader";
import CommonRestricted from '../../common/CommonRestricted';

import { useItems, itemTypes, targetTypes  } from "../../../hooks/api/useItems";
import { useSystemPrompt } from "../../../hooks/useSystemPrompt";
import { initialValues, validationSchema } from "../../../schema/products/brandSchema";
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(2)
}));

export default function NewBrand({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@product"] })

    const { promptMessage } = useSystemPrompt();
    const { isLoading, create } = useItems({ itemType: itemTypes.brand, targetType: targetTypes.brand });
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.product.id, scopeTypes.product.brand.id)

    const navigate = useNavigate();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const onSubmit = async (currentValues) => {

        try{
            
            let data = {
                item_brand: {
                    name: currentValues?.brandName,
                    description: currentValues?.description,
                    status: currentValues?.status? 1 : 0,
                }
            }

            const response = await create({ data });

            promptMessage(true, `${response?.message}`)
            navigate(getRoute("products/brands"));
                
        }catch(error){
            promptMessage(false, `Action Failed (${error?.message})`)
        }   

    }

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit
    });

    const { values, errors, touched, handleChange, setFieldValue } = formik;  

    const RenderForm = () => {
        return(
            
            <form onSubmit={formik.handleSubmit}>
                <StyledCard>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={4} textAlign={"left"}>
                            
                            <CommonSectionHeader
                                title={t('information')}
                                subTitle={t('provideDetailsOfTheProfile')}
                            />
                            
                        </Grid>
                        <Grid item xs={12} sm={12} md={8}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={12} md={6} textAlign="left">
                                        <FormControlLabel 
                                            control={
                                                <Switch 
                                                    name="status" 
                                                    checked={Boolean(values.status)}
                                                    onChange={(event, checked) => {
                                                        setFieldValue("status", checked);
                                                    }}
                                                />
                                            }                          
                                            label={       
                                                Boolean(values.status)? t('enabled') : t('disabled')
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={6}>    
                                        {/* Space down */}
                                    </Grid>

                                    <Grid item xs={12} sm={12} md={10}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            autoFocus
                                            fullWidth
                                            name={"brandName"}
                                            label={t('brandName')}
                                            type="text"                                         
                                            variant="outlined"
                                            value={values.brandName}
                                            onChange={handleChange}
                                            error={touched.brandName && Boolean(errors.brandName)}
                                            helperText={touched.brandName && errors.brandName}
                                        />
                                    </Grid>

                                    <Grid item xs={6}>    
                                        {/* Space down */}
                                    </Grid>
                    
                                    <Grid item xs={12} sm={12} md={10}>
                                        <TextField
                                            margin="dense"
                                            size="small"
                                            fullWidth
                                            name={"description"}
                                            label={t('description')}
                                            type="text"                                         
                                            variant="outlined"
                                            multiline
                                            rows={3}
                                            value={values.description}
                                            onChange={handleChange}
                                            error={touched.description && Boolean(errors.description)}
                                            helperText={touched.description && errors.description}
                                        />
                                    </Grid>
                            </Grid>
                        </Grid> 
                    </Grid>
                </StyledCard>
                <Grid container spacing={1} sx={{ pl: 2, pr: 2}}>
                    <Grid item md={12} textAlign={"right"}>
                        <LoadingButton
                            type="submit"
                            // fullWidth
                            variant="contained"
                            sx={{ 
                                mt: 3,
                                mb: 2
                            }}
                            loading={isLoading}
                        >
                            {t('saveChanges')}
                        </LoadingButton>
                    </Grid>
                </Grid>
            </form>
               
        )
    }

    return (
        <Box>
            {  
                getHtmlHead({ routeType, title: headTitles.newBrand })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('newBrand')}
                breadcrumbs={[
                    {
                        displayText: t('brands'),
                        props: {
                            href: getRoute("products/brands")
                        }
                    },
                    {
                        displayText: t('newBrand'),
                        props: {
                            color: 'secondary.main'
                        }
                    },
                ]}
            />

            { 
                isPermissionAllow({ functionType: functionTypes.pageNew.id })?
                    RenderForm() 
                    :
                    <CommonRestricted />
            }

        </Box>
    )
}